import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtCurrency, fmtDate, fmtDateRel } from "../../lib/api";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import {
    ArrowLeft, User, Phone, Mail, Calendar, CheckCircle2, Clock, AlertCircle,
    Zap, Repeat, MessageSquare, ArrowDownCircle, ArrowUpCircle, TrendingDown,
    FileWarning, Plus,
} from "lucide-react";

const STATUS_BG = {
    active: "bg-blue-50 text-blue-700 border-blue-200",
    repaid: "bg-emerald-50 text-emerald-700 border-emerald-200",
    overdue: "bg-rose-50 text-rose-700 border-rose-200",
    declined: "bg-slate-100 text-slate-700 border-slate-200",
    disbursed: "bg-blue-50 text-blue-700 border-blue-200",
};

const INSTALL_STATUS = {
    paid: { color: "#00D084", bg: "bg-emerald-50", text: "text-emerald-700", icon: CheckCircle2 },
    upcoming: { color: "#0066FF", bg: "bg-blue-50", text: "text-blue-700", icon: Clock },
    partial: { color: "#FF6B00", bg: "bg-amber-50", text: "text-amber-700", icon: AlertCircle },
    overdue: { color: "#FF0055", bg: "bg-rose-50", text: "text-rose-700", icon: AlertCircle },
};

const EVENT_META = {
    disbursed: { label: "Disbursed", icon: ArrowDownCircle, color: "#00D084" },
    repaid: { label: "Repaid in full", icon: CheckCircle2, color: "#00D084" },
    partial_repaid: { label: "Partial repayment", icon: ArrowUpCircle, color: "#0066FF" },
    auto_debited: { label: "Auto-debit", icon: Zap, color: "#FF6B00" },
    late_fee: { label: "Late fee applied", icon: TrendingDown, color: "#FF0055" },
    overdue: { label: "Marked overdue", icon: FileWarning, color: "#FF0055" },
    early_repaid: { label: "Early settlement", icon: Zap, color: "#7000FF" },
    restructured: { label: "Restructured", icon: Repeat, color: "#0066FF" },
    contacted: { label: "Collections contact", icon: Phone, color: "#06B6D4" },
};

const CONTACT_KINDS = [
    { value: "call", label: "Phone call", icon: Phone },
    { value: "sms", label: "SMS", icon: MessageSquare },
    { value: "whatsapp", label: "WhatsApp", icon: MessageSquare },
    { value: "visit", label: "Field visit", icon: User },
    { value: "promise_to_pay", label: "Promise to pay", icon: CheckCircle2 },
];

export default function AdminLoanDetail() {
    const { loanId } = useParams();
    const nav = useNavigate();
    const [loan, setLoan] = useState(null);
    const [schedule, setSchedule] = useState(null);
    const [statement, setStatement] = useState(null);
    const [actions, setActions] = useState([]);
    const [tab, setTab] = useState("schedule");
    const [restructureOpen, setRestructureOpen] = useState(false);
    const [contactOpen, setContactOpen] = useState(false);

    const load = async () => {
        const [s, st, ac] = await Promise.all([
            api.get(`/loans/${loanId}/schedule`),
            api.get(`/loans/${loanId}/statement`),
            api.get(`/admin/collections/${loanId}/actions`),
        ]);
        setSchedule(s.data);
        setStatement(st.data);
        setLoan(st.data.loan);
        setActions(ac.data);
    };

    useEffect(() => { load(); }, [loanId]);

    if (!loan) return <div className="p-12 text-center text-slate-400">Loading…</div>;

    const outstanding = statement.outstanding;
    const progress = Math.min(100, ((loan.repaid_amount || 0) / loan.total_repayment) * 100);

    return (
        <div data-testid="admin-loan-detail-page" className="space-y-5">
            <div className="flex items-center gap-3">
                <button onClick={() => nav(-1)} className="size-9 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="admin-loan-back">
                    <ArrowLeft size={18} />
                </button>
                <div>
                    <h1 className="font-display text-2xl font-semibold text-[#0A1930]">{loan.product}™ Loan</h1>
                    <p className="text-xs text-slate-500 font-mono">{loan.id}</p>
                </div>
                <div className="ml-auto flex gap-2">
                    <Button variant="outline" onClick={() => setContactOpen(true)} data-testid="log-contact-btn">
                        <Phone size={14} /> Log contact
                    </Button>
                    <Button variant="outline" onClick={() => setRestructureOpen(true)} data-testid="restructure-btn">
                        <Repeat size={14} /> Restructure
                    </Button>
                </div>
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Kpi label="Principal" value={fmtCurrency(loan.amount)} />
                <Kpi label="Total repayment" value={fmtCurrency(loan.total_repayment)} />
                <Kpi label="Outstanding" value={fmtCurrency(outstanding)} alert={outstanding > 0} />
                <Kpi label="Late fees" value={fmtCurrency(loan.late_fee_total || 0)} alert={(loan.late_fee_total || 0) > 0} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Left col: progress + meta */}
                <div className="lg:col-span-1 space-y-4">
                    <div className="bg-white rounded-2xl border border-slate-200 p-5 product-shadow">
                        <div className="flex justify-between items-center mb-3">
                            <Badge className={`capitalize border ${STATUS_BG[loan.status]}`}>{loan.status}</Badge>
                            <span className="text-xs text-slate-500">{loan.tenure_days}d tenure</span>
                        </div>
                        <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Repayment progress</div>
                        <div className="mt-2 h-3 rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full rounded-full bg-emerald-500" style={{ width: `${progress}%` }} />
                        </div>
                        <div className="flex justify-between text-xs mt-1.5 text-slate-500">
                            <span>{fmtCurrency(loan.repaid_amount || 0)} paid</span>
                            <span>{progress.toFixed(1)}%</span>
                        </div>

                        <div className="border-t border-slate-100 mt-4 pt-4 space-y-2 text-sm">
                            <Row label="Borrower" value={statement.loan.user_id ? <BorrowerInfo userId={statement.loan.user_id} /> : "—"} />
                            <Row label="Disbursed" value={fmtDate(loan.disbursed_at)} />
                            <Row label="Due date" value={fmtDate(loan.due_date)} />
                            <Row label="Interest" value={fmtCurrency(loan.interest)} />
                            <Row label="Fees" value={fmtCurrency(loan.fees)} />
                            {loan.restructured_at && <Row label="Restructured" value={fmtDateRel(loan.restructured_at)} />}
                            {loan.metadata?.farm_name && <Row label="Farm" value={loan.metadata.farm_name} />}
                            {loan.metadata?.vehicles && <Row label="Fleet size" value={loan.metadata.vehicles} />}
                        </div>
                    </div>
                </div>

                {/* Right col: tabs */}
                <div className="lg:col-span-2 space-y-4">
                    <Tabs value={tab} onValueChange={setTab}>
                        <TabsList className="bg-white border border-slate-200 rounded-xl p-1 grid grid-cols-3 max-w-md" data-testid="admin-loan-tabs">
                            <TabsTrigger value="schedule" data-testid="admin-tab-schedule">Schedule</TabsTrigger>
                            <TabsTrigger value="statement" data-testid="admin-tab-statement">Statement</TabsTrigger>
                            <TabsTrigger value="contacts" data-testid="admin-tab-contacts">Contacts ({actions.length})</TabsTrigger>
                        </TabsList>
                    </Tabs>

                    {tab === "schedule" && (
                        <div className="bg-white rounded-2xl border border-slate-200 product-shadow overflow-hidden" data-testid="admin-schedule">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50">
                                    <tr className="text-left text-xs uppercase tracking-wider font-bold text-slate-500">
                                        <th className="px-4 py-3">#</th>
                                        <th className="px-4 py-3">Due</th>
                                        <th className="px-4 py-3">Principal</th>
                                        <th className="px-4 py-3">Interest</th>
                                        <th className="px-4 py-3">Fees</th>
                                        <th className="px-4 py-3">Total</th>
                                        <th className="px-4 py-3">Paid</th>
                                        <th className="px-4 py-3">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {schedule.items.map((it) => {
                                        const meta = INSTALL_STATUS[it.status] || INSTALL_STATUS.upcoming;
                                        return (
                                            <tr key={it.n} className="border-b border-slate-50" data-testid={`admin-install-${it.n}`}>
                                                <td className="px-4 py-3 font-bold">{it.n}</td>
                                                <td className="px-4 py-3 text-xs text-slate-600">{fmtDate(it.due_date)}</td>
                                                <td className="px-4 py-3 font-mono text-xs">{fmtCurrency(it.principal)}</td>
                                                <td className="px-4 py-3 font-mono text-xs">{fmtCurrency(it.interest)}</td>
                                                <td className="px-4 py-3 font-mono text-xs">{fmtCurrency(it.fees)}</td>
                                                <td className="px-4 py-3 font-mono font-semibold">{fmtCurrency(it.amount)}</td>
                                                <td className="px-4 py-3 font-mono text-xs text-emerald-600">{fmtCurrency(it.paid_amount || 0)}</td>
                                                <td className="px-4 py-3">
                                                    <Badge className={`capitalize border ${meta.bg} ${meta.text} border-current/20`}>{it.status}</Badge>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {tab === "statement" && (
                        <div className="bg-white rounded-2xl border border-slate-200 product-shadow overflow-hidden" data-testid="admin-statement">
                            {statement.events.length === 0 && <div className="p-8 text-center text-sm text-slate-500">No events</div>}
                            {statement.events.map((e, i) => {
                                const m = EVENT_META[e.kind] || { label: e.kind, icon: AlertCircle, color: "#64748B" };
                                const Icon = m.icon;
                                return (
                                    <div key={e.id || i} className={`p-4 flex items-start gap-3 ${i !== statement.events.length - 1 ? "border-b border-slate-100" : ""}`} data-testid={`admin-event-${e.kind}-${i}`}>
                                        <div className="size-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${m.color}15`, color: m.color }}>
                                            <Icon size={18} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold text-sm">{m.label}</div>
                                            {e.description && <div className="text-xs text-slate-600 mt-0.5">{e.description}</div>}
                                            <div className="text-[10px] text-slate-400 mt-1">{new Date(e.created_at).toLocaleString()} · {e.actor || "system"}</div>
                                        </div>
                                        {e.amount > 0 && (
                                            <div className="font-mono font-bold text-sm" style={{ color: m.color }}>{fmtCurrency(e.amount)}</div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {tab === "contacts" && (
                        <div className="bg-white rounded-2xl border border-slate-200 product-shadow overflow-hidden" data-testid="admin-contacts">
                            {actions.length === 0 && (
                                <div className="p-8 text-center text-sm text-slate-500">
                                    No collections contacts yet. Click <strong>Log contact</strong> to record one.
                                </div>
                            )}
                            {actions.map((a, i) => {
                                const k = CONTACT_KINDS.find((c) => c.value === a.kind);
                                const Icon = k?.icon || MessageSquare;
                                return (
                                    <div key={a.id} className={`p-4 flex items-start gap-3 ${i !== actions.length - 1 ? "border-b border-slate-100" : ""}`} data-testid={`admin-action-${a.id}`}>
                                        <div className="size-10 rounded-xl bg-cyan-50 text-cyan-600 flex items-center justify-center flex-shrink-0">
                                            <Icon size={18} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold text-sm flex items-center gap-2">
                                                {k?.label || a.kind}
                                                {a.amount_promised && (
                                                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                                                        Promise: {fmtCurrency(a.amount_promised)}
                                                    </Badge>
                                                )}
                                            </div>
                                            <div className="text-xs text-slate-600 mt-1">{a.note}</div>
                                            <div className="text-[10px] text-slate-400 mt-1">{new Date(a.contacted_at).toLocaleString()} · {a.agent_email}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <RestructureDialog open={restructureOpen} onClose={() => setRestructureOpen(false)} loan={loan} onDone={load} />
            <ContactDialog open={contactOpen} onClose={() => setContactOpen(false)} loanId={loanId} onDone={load} />
        </div>
    );
}

function Kpi({ label, value, alert }) {
    return (
        <div className={`bg-white rounded-2xl border p-4 product-shadow ${alert ? "border-rose-200" : "border-slate-200"}`}>
            <div className="text-xs uppercase tracking-wider font-bold text-slate-500">{label}</div>
            <div className={`font-display text-2xl font-bold mt-2 ${alert ? "text-rose-600" : "text-[#0A1930]"}`}>{value}</div>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex justify-between gap-3">
            <span className="text-slate-500 text-sm">{label}</span>
            <span className="font-semibold text-sm text-right truncate max-w-[200px]">{value}</span>
        </div>
    );
}

function BorrowerInfo({ userId }) {
    const [user, setUser] = useState(null);
    useEffect(() => {
        api.get("/admin/users").then((r) => {
            setUser(r.data.find((u) => u.id === userId));
        });
    }, [userId]);
    if (!user) return "—";
    return (
        <span className="flex items-center gap-1.5">
            {user.full_name}
            {user.phone && <Phone size={10} className="text-slate-400" />}
        </span>
    );
}

function RestructureDialog({ open, onClose, loan, onDone }) {
    const [form, setForm] = useState({ extend_days: 30, waive_fees: false, waive_late_fees: true, new_rate: "", note: "" });
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        setBusy(true);
        try {
            await api.post(`/admin/loans/${loan.id}/restructure`, {
                extend_days: parseInt(form.extend_days || "0", 10),
                waive_fees: form.waive_fees,
                waive_late_fees: form.waive_late_fees,
                new_rate: form.new_rate ? parseFloat(form.new_rate) : null,
                note: form.note,
            });
            toast.success("Loan restructured");
            onDone();
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    };
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-2xl">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2"><Repeat size={18} className="text-[#0066FF]" /> Restructure loan</DialogTitle>
                    <DialogDescription>Extend tenure, adjust the rate, or waive fees. The borrower will be notified and a new schedule will be generated.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">Extend tenure by (days)</Label>
                        <Input type="number" value={form.extend_days} onChange={(e) => setForm((s) => ({ ...s, extend_days: e.target.value }))} className="mt-1 h-11 rounded-xl" data-testid="restructure-extend" />
                    </div>
                    <div>
                        <Label className="text-xs">New monthly interest rate (optional, e.g. 0.08 = 8%)</Label>
                        <Input type="number" step="0.01" placeholder="Leave blank to keep current" value={form.new_rate} onChange={(e) => setForm((s) => ({ ...s, new_rate: e.target.value }))} className="mt-1 h-11 rounded-xl" data-testid="restructure-rate" />
                    </div>
                    <div className="flex items-center justify-between bg-slate-50 rounded-xl p-3">
                        <Label htmlFor="waive_fees" className="text-sm">Waive origination fees</Label>
                        <Switch id="waive_fees" checked={form.waive_fees} onCheckedChange={(v) => setForm((s) => ({ ...s, waive_fees: v }))} data-testid="restructure-waive-fees" />
                    </div>
                    <div className="flex items-center justify-between bg-slate-50 rounded-xl p-3">
                        <Label htmlFor="waive_late" className="text-sm">Waive late fees</Label>
                        <Switch id="waive_late" checked={form.waive_late_fees} onCheckedChange={(v) => setForm((s) => ({ ...s, waive_late_fees: v }))} data-testid="restructure-waive-late" />
                    </div>
                    <div>
                        <Label className="text-xs">Note (audit trail)</Label>
                        <Textarea rows={3} value={form.note} onChange={(e) => setForm((s) => ({ ...s, note: e.target.value }))} placeholder="Reason for restructuring…" className="mt-1 rounded-xl" data-testid="restructure-note" />
                    </div>
                    <Button onClick={submit} disabled={busy} className="w-full h-11 rounded-xl bg-[#0066FF] hover:bg-[#0052cc]" data-testid="restructure-submit">
                        {busy ? "Restructuring…" : "Apply restructure"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function ContactDialog({ open, onClose, loanId, onDone }) {
    const [form, setForm] = useState({ kind: "call", note: "", amount_promised: "" });
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (!form.note) return toast.error("Add a note");
        setBusy(true);
        try {
            await api.post(`/admin/collections/${loanId}/actions`, {
                kind: form.kind,
                note: form.note,
                amount_promised: form.amount_promised ? parseFloat(form.amount_promised) : null,
            });
            toast.success("Contact logged");
            setForm({ kind: "call", note: "", amount_promised: "" });
            onDone();
            onClose();
        } catch (err) {
            toast.error("Failed");
        } finally {
            setBusy(false);
        }
    };
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-2xl">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2"><Phone size={18} className="text-cyan-600" /> Log collections contact</DialogTitle>
                    <DialogDescription>Record an outreach attempt (call, SMS, WhatsApp, field visit, or promise-to-pay). It's added to the loan's audit trail.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">Type</Label>
                        <Select value={form.kind} onValueChange={(v) => setForm((s) => ({ ...s, kind: v }))}>
                            <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid="contact-kind"><SelectValue /></SelectTrigger>
                            <SelectContent>{CONTACT_KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs">Note</Label>
                        <Textarea rows={3} value={form.note} onChange={(e) => setForm((s) => ({ ...s, note: e.target.value }))} placeholder="What was discussed?" className="mt-1 rounded-xl" data-testid="contact-note" />
                    </div>
                    <div>
                        <Label className="text-xs">Amount promised (optional)</Label>
                        <Input type="number" value={form.amount_promised} onChange={(e) => setForm((s) => ({ ...s, amount_promised: e.target.value }))} placeholder="₦" className="mt-1 h-11 rounded-xl" data-testid="contact-amount" />
                    </div>
                    <Button onClick={submit} disabled={busy} className="w-full h-11 rounded-xl bg-cyan-600 hover:bg-cyan-700" data-testid="contact-submit">
                        {busy ? "Saving…" : "Save contact"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
